import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Eye, Users, ShieldCheck, FileX, TrendingUp, Award, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import Founders from "@/components/home/Founders";
import { IMAGES } from "@/data/site";

const RHYTHM = [
  { n: "01", title: "Discovery", desc: "A senior leadership call — we start by listening. Your full picture, your ambitions, your constraints, and the specific gap between where you are and where you want to be. No assumptions, no agenda." },
  { n: "02", title: "Strategy", desc: "We build a focused, personalised plan. Not a generic roadmap — a strategy designed around your target role or hiring goal, your industry, and your timeline." },
  { n: "03", title: "Execution", desc: "We build your career assets, coach your interviews, support your search, or run your recruitment process — doing the real work alongside you, with founding team attention at every step." },
  { n: "04", title: "Growth", desc: "We stay through the outcome and beyond. Momentum that outlasts the engagement and compounds into the next chapter." },
];

const MISSION_VISION = [
  {
    icon: Target,
    title: "Mission",
    points: [
      "Help clients move forward — professionals and organisations alike",
      "A professional building a career worth being proud of",
      "An organisation building a team that lasts",
    ],
  },
  {
    icon: Eye,
    title: "Vision",
    points: [
      "Be the growth partner clients trust most",
      "The one they return to at every inflection point",
      "Recommended without hesitation",
    ],
  },
];

const VALUES = [
  { icon: Users, title: "Clients first", desc: "Every strategy centres on the client's goals, whether professional or organisational." },
  { icon: ShieldCheck, title: "Radical honesty", desc: "Including about ourselves and where we are in our journey." },
  { icon: FileX, title: "No templates", desc: "Every engagement is built from scratch." },
  { icon: TrendingUp, title: "Outcomes over deliverables", desc: "Success is measured by results, not documents sent." },
  { icon: Award, title: "Earn trust, don't claim it", desc: "We build our reputation engagement by engagement." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us"
        description="Meet the founding team behind North Cadence — a career growth and talent services firm built on real hiring and career experience, not templates or invented credentials."
      />
      <PageHero
        eyebrow="About Us"
        title={["We help clients grow —", "careers and companies."]}
        subtitle="North Cadence is a career growth and talent services firm. Our primary work is with individual professionals. We also work with organisations on recruitment, RPO, talent acquisition, business development, hiring strategy & workforce planning, and HR & payroll. Both practices are led by the same founding team — with the same commitment to precision, honesty, and outcomes."
      />

      {/* Story */}
      <section className="bg-white py-8">
        <div className="nc-container grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-[1.75rem] clip-arch">
              <img src={IMAGES.heroOffice} alt="North Cadence" className="w-full object-contain" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Our Story"
              title={<>Built by people who've sat on <span className="text-[#142A5E]">both sides of the hiring table.</span></>}
            />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-5 text-lg leading-relaxed text-[#1B1B1B]/65">
                <p>
                  North Cadence was founded by <strong className="text-[#1B1B1B]">Kesha Desai</strong> and{" "}
                  <strong className="text-[#1B1B1B]">Dr. Abhishek Gandhi</strong> on a single conviction: that most professionals with real ability are being let down by generic templates, impersonal services, and career advice that has never been stress-tested against how hiring actually works.
                </p>
                <p>
                  Between them, the founding team brings 20+ years of experience across global enterprise sales, IT staffing and consulting, healthcare SaaS product leadership, and AI — spanning both sides of the hiring table. That combination is not incidental. It is the reason North Cadence can advise professionals with a hiring manager's perspective, and advise organisations with an understanding of what candidates actually experience.
                </p>
                <p>
                  We are a new firm. We launched because we saw the gap clearly, and we have the background to close it. Our reputation will be built engagement by engagement — and we are committed to earning it.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission Vision Values */}
      <section className="bg-[#FEF7F2] py-8">
        <div className="nc-container">
          <SectionHeading align="center" eyebrow="What Drives Us" title={<>Mission & <span className="text-[#2FA86A]">vision.</span></>} />
          <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {MISSION_VISION.map((v) => (
              <StaggerItem key={v.title}>
                <div className="h-full rounded-3xl border border-[#e7e9ef] bg-white p-9">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#142A5E]/8 text-[#142A5E]">
                    <v.icon className="h-7 w-7" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold text-[#1B1B1B]">{v.title}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {v.points.map((point) => (
                      <li key={point} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-[#1B1B1B]/65">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2FA86A]" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Values */}
      <section className="relative overflow-hidden bg-[#142A5E] py-8">
        <div className="pointer-events-none absolute -right-24 top-0 h-96 w-96 rounded-full bg-[#2FA86A]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
        <div className="nc-container relative">
          <SectionHeading
            align="center"
            light
            eyebrow="What We Stand For"
            title="Values."
            subtitle="These are not aspirations. They are the operating beliefs behind every engagement we take on."
          />
          <StaggerGroup className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
            {VALUES.map((v, i) => (
              <StaggerItem key={v.title}>
                <div className="group flex h-full flex-col gap-4 bg-[#142A5E] p-7 transition-colors duration-300 hover:bg-[#0d1c40]">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#2FA86A] transition-colors duration-300 group-hover:bg-[#2FA86A] group-hover:text-white">
                      <v.icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <span className="font-display text-3xl font-extrabold text-white/10">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold leading-snug text-white">{v.title}</h3>
                  <p className="text-sm leading-relaxed text-white/60">{v.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* How We Work */}
      <section className="bg-white py-8">
        <div className="nc-container">
          <SectionHeading eyebrow="How We Work" title={<>The North Cadence <span className="text-[#142A5E]">rhythm.</span></>} />
          <StaggerGroup className="mt-14 space-y-px overflow-hidden rounded-3xl border border-[#e7e9ef]">
            {RHYTHM.map((p) => (
              <StaggerItem key={p.n}>
                <div className="group grid gap-4 bg-white p-8 transition-colors hover:bg-[#FEF7F2] md:grid-cols-[120px_1fr_1.6fr] md:items-center">
                  <span className="font-display text-5xl font-extrabold text-[#142A5E]/15 transition-colors group-hover:text-[#2FA86A]/50">{p.n}</span>
                  <h3 className="font-display text-2xl font-bold text-[#1B1B1B]">{p.title}</h3>
                  <p className="leading-relaxed text-[#1B1B1B]/60">{p.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* <Stats /> */}
      <Founders />

      <section className="bg-white py-8">
        <div className="nc-container flex flex-col items-center gap-6 text-center">
          <SectionHeading align="center" eyebrow="Want To Work With Us?" title="We're deliberate about every engagement we take on." subtitle="Book a discovery call and let's find out if we're the right fit for each other." />
          <Reveal delay={0.1}>
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-[#142A5E] px-7 py-4 font-semibold text-white transition-colors hover:bg-[#0d1c40]">
              Book a Discovery Call <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
